import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { X, Plus, FilePlus, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmProvider";

function splitPosts(raw) {
  if (!raw) return [];
  let parts = raw.split(/\n\s*(?:---+|===+|\*\*\*+)\s*\n/g).map(s => s.trim()).filter(Boolean);
  if (parts.length > 1) return parts;
  parts = raw.split(/\n\s*(?:Post\s*#?\s*\d+[:.)]?|\d+[.)])\s*\n/gi).map(s => s.trim()).filter(Boolean);
  if (parts.length > 1) return parts;
  parts = raw.split(/\n{3,}/g).map(s => s.trim()).filter(Boolean);
  if (parts.length > 1) return parts;
  return [raw.trim()];
}

// Field extracted OUTSIDE the parent component so it doesn't remount every render
function Field({ label, hint, children }) {
  return (
    <div className="mb-6">
      <div className="eyebrow mb-2">{label}</div>
      {children}
      {hint && <div className="text-xs mt-2" style={{color:'var(--ink-3)'}}>{hint}</div>}
    </div>
  );
}

const EMPTY = {
  positioning: "", niche: "", audience: "", pillars: [], tone: [],
  goals: "", offer: "", credibility: "", never_say: [], writing_samples: "",
};

export default function IdentityPage() {
  const confirm = useConfirm();
  const [f, setF] = useState(EMPTY);
  const [pillarsStr, setPillarsStr] = useState("");
  const [toneStr, setToneStr] = useState("");
  const [neverStr, setNeverStr] = useState("");
  const [samples, setSamples] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkSource, setBulkSource] = useState("");
  const [addOne, setAddOne] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const saveTimer = useRef(null);
  const savedOnceRef = useRef(false);

  // Initial hydration
  useEffect(() => {
    (async () => {
      const r = await api.get("/identity");
      setF({ ...EMPTY, ...r.data });
      setPillarsStr((r.data.pillars || []).join(", "));
      setToneStr((r.data.tone || []).join(", "));
      setNeverStr((r.data.never_say || []).join(", "));
      setSamples(splitPosts(r.data.writing_samples || ""));
      setHydrated(true);
    })();
  }, []);

  // Debounced autosave
  useEffect(() => {
    if (!hydrated) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = setTimeout(async () => {
      try {
        const payload = {
          ...f,
          pillars: pillarsStr.split(",").map(s=>s.trim()).filter(Boolean),
          tone:    toneStr.split(",").map(s=>s.trim()).filter(Boolean),
          never_say: neverStr.split(",").map(s=>s.trim()).filter(Boolean),
          writing_samples: samples.join("\n\n---\n\n"),
        };
        await api.put("/identity", payload);
        setSaveState("saved");
        if (!savedOnceRef.current) {
          savedOnceRef.current = true;
        }
      } catch (e) {
        toast.error(formatApiError(e));
        setSaveState("idle");
      }
    }, 900);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, pillarsStr, toneStr, neverStr, samples, hydrated]);

  const importBulk = () => {
    const parts = splitPosts(bulkText);
    if (!parts.length) { toast.error("Nothing to import."); return; }
    setSamples(prev => [...prev, ...parts]);
    setBulkText("");
    setBulkSource("");
    setShowBulk(false);
    toast.success(`Imported ${parts.length} item${parts.length===1?"":"s"} as chips.`);
  };

  const extractFile = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/identity/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkText(r.data.text);
      setBulkSource(r.data.filename);
      setShowBulk(true);
      const preview = splitPosts(r.data.text);
      toast.success(`Extracted ${r.data.chars.toLocaleString()} chars from ${r.data.filename}. ${preview.length} item${preview.length===1?"":"s"} detected. Review below.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExtracting(false); }
  };

  const addOneChip = () => {
    if (!addOne.trim()) return;
    setSamples(prev => [...prev, addOne.trim()]);
    setAddOne("");
  };
  const removeChip = async (i) => {
    const s = samples[i];
    const preview = (s || "").slice(0, 80).replace(/\n/g, " ") + ((s || "").length > 80 ? "…" : "");
    const ok = await confirm({
      title: "Remove this writing sample?",
      description: `You'll lose "${preview}". She uses these samples to learn your voice. This isn't sent to Trash.`,
      confirmText: "Remove sample",
    });
    if (!ok) return;
    setSamples(prev => prev.filter((_, idx) => idx !== i));
  };

  const saveBadge = (
    <span className="chip" data-testid="identity-save-state">
      {saveState === "saving" && <><CircleNotch size={12} className="animate-spin"/> Saving…</>}
      {saveState === "saved"  && <><CheckCircle size={12} weight="fill" style={{color:'#4a7c3f'}}/> Autosaved</>}
      {saveState === "idle"   && <span style={{color:'var(--ink-3)'}}>Autosave on</span>}
    </span>
  );

  return (
    <div>
      <PageHeader
        eyebrow="About me"
        title="Who she works for."
        subtitle="Structured identity. The positioning, pillars, and rules the assistant references every single draft. Editable any time. Every change autosaves."
        action={saveBadge}
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="font-serif text-2xl mb-6 font-semibold">Positioning</h3>
          <Field label="Positioning statement" hint="One line, who you are, what you do, who you serve.">
            <textarea data-testid="id-positioning" className="textarea min-h-[80px]" value={f.positioning||""} onChange={e=>setF({...f, positioning:e.target.value})} placeholder="I help B2B founders build a personal brand that generates inbound." />
          </Field>
          <Field label="Niche">
            <input data-testid="id-niche" className="input" value={f.niche||""} onChange={e=>setF({...f, niche:e.target.value})} placeholder="B2B SaaS, LATAM" />
          </Field>
          <Field label="Target audience / ICP">
            <textarea data-testid="id-audience" className="textarea min-h-[80px]" value={f.audience||""} onChange={e=>setF({...f, audience:e.target.value})} placeholder="Founders and heads of marketing at $1M-$50M ARR B2B companies…" />
          </Field>
          <Field label="Content pillars (comma-separated)" hint="3-5 topics you'll be known for.">
            <input data-testid="id-pillars" className="input" value={pillarsStr} onChange={e=>setPillarsStr(e.target.value)} placeholder="Positioning, Personal brand, Content ops, Growth" />
          </Field>
          <Field label="Tone descriptors (comma-separated)">
            <input data-testid="id-tone" className="input" value={toneStr} onChange={e=>setToneStr(e.target.value)} placeholder="Direct, warm, contrarian, editorial" />
          </Field>
        </div>

        <div className="card p-6">
          <h3 className="font-serif text-2xl mb-6 font-semibold">Rules & context</h3>
          <Field label="Goals">
            <textarea data-testid="id-goals" className="textarea min-h-[80px]" value={f.goals||""} onChange={e=>setF({...f, goals:e.target.value})} placeholder="10k followers, 20 qualified inbounds/mo, become the authority on…" />
          </Field>
          <Field label="Offer / services">
            <textarea data-testid="id-offer" className="textarea min-h-[60px]" value={f.offer||""} onChange={e=>setF({...f, offer:e.target.value})} placeholder="1:1 coaching, done-for-you brand ops" />
          </Field>
          <Field label="Credibility markers">
            <textarea data-testid="id-credibility" className="textarea min-h-[60px]" value={f.credibility||""} onChange={e=>setF({...f, credibility:e.target.value})} placeholder="Ex-Google, 3x founder, TEDx…" />
          </Field>
          <Field label="Never say (comma-separated)" hint="Taboo topics, banned words, competitors not to mention.">
            <input data-testid="id-neversay" className="input" value={neverStr} onChange={e=>setNeverStr(e.target.value)} placeholder="crypto shilling, guaranteed results" />
          </Field>
        </div>
      </div>

      <div className="card p-6 mt-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="eyebrow mb-2">Writing style training</div>
            <h3 className="font-serif text-2xl font-semibold">Teach her your voice.</h3>
            <p className="text-sm mt-2 max-w-2xl" style={{color:'var(--ink-2)'}}>
              Add 10-20 of your best posts as individual chips. She'll learn your vocabulary, sentence rhythm, hooks, and how you open and close. Bulk-paste or upload a Word / PDF / text file.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="btn btn-ghost cursor-pointer" data-testid="id-upload-label">
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt,.md"
                className="hidden"
                data-testid="id-upload-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) extractFile(file);
                  e.target.value = "";
                }}
              />
              {extracting ? <><CircleNotch size={12} className="animate-spin"/> Extracting</> : (<><FilePlus size={14}/> Import from file</>)}
            </label>
            <button data-testid="id-bulk-btn" className="btn btn-ghost" onClick={()=>{ setBulkSource(""); setBulkText(""); setShowBulk(true); }}><Plus size={14}/> Bulk paste</button>
          </div>
        </div>

        <div className="space-y-3">
          {samples.map((s, i) => (
            <div key={i} data-testid={`id-sample-${i}`} className="p-4 relative fade-up rounded-2xl" style={{ background: "var(--bg-soft)" }}>
              <div className="flex justify-between items-start gap-3">
                <div className="eyebrow" style={{ color: "var(--accent)" }}>Post {i+1}</div>
                <button data-testid={`id-sample-remove-${i}`} onClick={()=>removeChip(i)} className="p-1.5 rounded-full hover:bg-white transition-colors"><X size={12}/></button>
              </div>
              <textarea
                value={s}
                onChange={e=>{
                  const next = [...samples];
                  next[i] = e.target.value;
                  setSamples(next);
                }}
                className="w-full mt-2 bg-transparent text-sm font-mono resize-none min-h-[100px] outline-none"
                data-testid={`id-sample-text-${i}`}
              />
            </div>
          ))}

          <div className="p-4 rounded-2xl" style={{ background: "var(--bg-soft)" }}>
            <div className="eyebrow mb-2">Add one post</div>
            <textarea data-testid="id-sample-add-input" value={addOne} onChange={e=>setAddOne(e.target.value)} className="textarea min-h-[80px] font-mono text-xs" placeholder="Paste one of your posts here…" />
            <button data-testid="id-sample-add-btn" className="btn btn-ghost mt-3" onClick={addOneChip} disabled={!addOne.trim()}><Plus size={14}/> Add as chip</button>
          </div>
        </div>
      </div>

      {showBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.55)'}} onClick={()=>setShowBulk(false)}>
          <div onClick={e=>e.stopPropagation()} className="card p-6 w-full max-w-3xl" data-testid="id-bulk-modal">
            <div className="eyebrow mb-2">{bulkSource ? `Import from ${bulkSource}` : "Bulk paste"}</div>
            <h3 className="font-serif text-3xl mb-2 font-semibold">Review before adding.</h3>
            <p className="text-sm mb-3" style={{color:'var(--ink-2)'}}>
              {bulkSource
                ? "We extracted the text from your file. Edit if needed. Each block becomes one chip. Separators: --- on its own line, ===, ***, or 3+ blank lines. 'Post N:' and '1.' style dividers also work."
                : "Paste posts with --- on its own line, or a blank line between them. We'll split each into its own chip."}
            </p>
            <div className="flex items-center gap-3 mb-2 text-xs font-mono" style={{color:'var(--ink-3)'}}>
              <span data-testid="id-bulk-chars">{bulkText.length.toLocaleString()} chars</span>
              <span>·</span>
              <span data-testid="id-bulk-count"><b style={{color:'var(--accent)'}}>{splitPosts(bulkText).length}</b> item{splitPosts(bulkText).length===1?"":"s"} detected</span>
            </div>
            <textarea data-testid="id-bulk-textarea" value={bulkText} onChange={e=>setBulkText(e.target.value)} className="textarea min-h-[340px] font-mono text-xs" placeholder={"Post 1, hook, body\n\n---\n\nPost 2, hook, body"} />
            <div className="flex justify-end gap-3 mt-4">
              <button className="btn btn-ghost" onClick={()=>setShowBulk(false)}>Cancel</button>
              <button data-testid="id-bulk-submit" onClick={importBulk} disabled={!bulkText.trim()} className="btn btn-accent">Import {splitPosts(bulkText).length || 0} as chips</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
